import { valibotResolver } from '@hookform/resolvers/valibot';
import { Link, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { format, formatDistanceToNow } from 'date-fns';
import { ChevronDownIcon, InfoIcon, MailPlusIcon, PencilIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  maxLength,
  minLength,
  object,
  optional,
  pipe,
  string,
  type InferInput as ValibotInput,
} from 'valibot';
import { useGetComments } from '@/api/comments/queries';
import { useRemoveRequest, useUpdateRequest } from '@/api/requests/queries';
import type { Request } from '@/api/requests/types';
import { useGetSubscription } from '@/api/subscription/queries';
import { CopyToClipboardButton } from '@/components/copy-to-clipboard-button';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { SheetHeader } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { UpgradeLink } from '@/components/upgrade-link';
import { UserAvatar } from '@/components/user-avatar';
import { ConfirmDeleteAlert } from '@/components/v1/confirm-delete-alert';
import { EMPTY_UUID } from '@/utils';
import { PRIORITIES, STATUSES } from '../-utils/config';
import { ChangePriorityDropdown } from './change-priority-dropdown';
import { ChangeStatusDropdown } from './change-status-dropdown';
import { RequestComments } from './request-comments';
import { RequestPlatform } from './request-platform';
import { RequestVoteCount } from './request-vote-count';

type Props = {
  request: Request;
  onRequestDelete: () => void;
};

const FormSchema = object({
  name: pipe(
    string(),
    minLength(1, 'Please enter Request title'),
    maxLength(50, 'Title cannot exceed 50 symbols'),
  ),
  description: pipe(
    string(),
    minLength(10, 'Please enter Request description'),
    maxLength(255, 'Description cannot exceed 255 symbols'),
  ),
  note: optional(string(), ''),
  publicNote: optional(string(), ''),
});

type FormDataType = ValibotInput<typeof FormSchema>;

export const RequestSheetContent = ({ request, onRequestDelete }: Props) => {
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const [requestToRemove, setRequestToRemove] = useState<Request | null>(null);
  const updateMutation = useUpdateRequest();
  const removeMutation = useRemoveRequest();
  const [isEditing, setIsEditing] = useState(false);
  const { data: subscription, isPending: isSubscriptionPending } = useGetSubscription();
  const [isRequestDetailsOpen, setIsRequestDetailsOpen] = useState(true);
  const isFreePlan = subscription?.isFree;

  const { data: comments, isFetching: isCommentsFetching } = useGetComments(projectId, request.id, {
    enabled: !subscription?.isFree,
  });

  const form = useForm<FormDataType>({
    resolver: valibotResolver(FormSchema),
    defaultValues: {
      name: request.name,
      description: request.description,
      note: request.note,
      publicNote: request.publicNote,
    },
  });

  const onSubmit = (data: FormDataType) => {
    return updateMutation.mutateAsync(
      {
        requestId: request.id,
        projectId: request.projectId,
        data: { ...request, ...data },
      },
      {
        onSuccess: () => {
          toast.success('Request updated');
          setIsEditing(false);
          form.reset({ ...data });
        },
      },
    );
  };

  const handleChangeStatus = (request: Request, status: string) => {
    return updateMutation.mutateAsync(
      {
        requestId: request.id,
        projectId: request.projectId,
        data: {
          name: request.name,
          description: request.description,
          status,
          note: request.note,
          publicNote: request.publicNote,
          priority: request.priority,
        },
      },
      { onSuccess: () => toast.success('Request status updated') },
    );
  };

  const handleChangePriority = (request: Request, priority: string) => {
    return updateMutation.mutateAsync(
      {
        requestId: request.id,
        projectId: request.projectId,
        data: {
          name: request.name,
          description: request.description,
          status: request.status,
          note: request.note,
          publicNote: request.publicNote,
          priority,
        },
      },
      { onSuccess: () => toast.success('Request priority updated') },
    );
  };

  const handleDeleteAction = (confirmed: boolean) => {
    if (!confirmed) {
      return setRequestToRemove(null);
    }

    if (!requestToRemove) {
      return;
    }

    setRequestToRemove(null);
    return removeMutation.mutateAsync(
      { requestId: requestToRemove.id, projectId: requestToRemove.projectId },
      {
        onSuccess: () => {
          toast.success('Request removed');
          onRequestDelete();
        },
      },
    );
  };

  const handleCancelEditClick = () => {
    setIsEditing(false);
    form.reset();
  };

  const status = Object.values(STATUSES).find((status) => status.status === request.status);
  const priority = Object.values(PRIORITIES).find(
    (priority) => priority.status === request.priority,
  );

  return (
    <>
      <SheetHeader className="gap-4 border-b-1">
        {isEditing ? (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 pt-8">
              <div className="flex flex-1 flex-col gap-5">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="gap-0.5">
                      <FormLabel className="text-muted-foreground text-sm sr-only">Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Request title" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem className="gap-0.5">
                      <FormLabel className="text-muted-foreground text-sm sr-only">
                        Description
                      </FormLabel>
                      <FormControl>
                        <Textarea placeholder="Request description" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {isFreePlan ? (
                  <div className="flex flex-col gap-1 items-start">
                    <p className="text-muted-foreground text-xs">
                      Upgrade to add private and public notes.
                    </p>
                    <UpgradeLink variant="small">Unlock notes</UpgradeLink>
                  </div>
                ) : (
                  <>
                    <FormField
                      disabled={isFreePlan}
                      control={form.control}
                      name="note"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-muted-foreground">Private Note</FormLabel>
                          <FormControl>
                            <Textarea
                              className="text-sm min-h-[64px] py-1"
                              placeholder="This is an internal note for your team. Users won't see this."
                              {...field}
                              {...(isFreePlan ? { value: '' } : undefined)}
                            />
                          </FormControl>
                          <p className="text-muted-foreground text-xs relative top-[-4px]">
                            Internal note for your team only.
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      disabled={isFreePlan}
                      control={form.control}
                      name="publicNote"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-muted-foreground">Public Note</FormLabel>
                          <FormControl>
                            <Textarea
                              className="text-sm min-h-[64px] py-1"
                              placeholder="This note is visible for all users in request details."
                              {...field}
                              {...(isFreePlan ? { value: '' } : undefined)}
                            />
                          </FormControl>
                          <p className="text-muted-foreground text-xs relative top-[-4px]">
                            Visible note for all users.
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  className="self-end"
                  variant="ghost"
                  onClick={handleCancelEditClick}
                >
                  Cancel
                </Button>
                <Button
                  className="self-end"
                  type="submit"
                  disabled={form.formState.isSubmitting || !form.formState.isDirty}
                >
                  Save changes
                </Button>
              </div>
            </form>
          </Form>
        ) : (
          <>
            <div className="flex flex-1 flex-col gap-5 pt-4 pb-2">
              <span className="text-xl font-bold">{request.name}</span>
              <span className="">{request.description}</span>
              {isFreePlan ? (
                <div className="flex flex-col gap-1 items-start">
                  <p className="text-muted-foreground text-xs">
                    Upgrade to add private and public notes.
                  </p>
                  <UpgradeLink variant="small">Unlock notes</UpgradeLink>
                </div>
              ) : (
                <>
                  <div className="flex flex-col">
                    <Label className="text-muted-foreground text-sm">Private note</Label>
                    <span className={clsx(!request.note && 'text-xs text-muted-foreground')}>
                      {request.note || 'Private note is not added yet'}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <Label className="text-muted-foreground text-sm">Public note</Label>
                    <span className={clsx(!request.publicNote && 'text-xs text-muted-foreground')}>
                      {request.publicNote || 'Public note is not added yet'}
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="flex flex-row items-center justify-between gap-2">
              <ConfirmDeleteAlert
                title="Delete Request"
                description="This action cannot be undone. This will permanently delete this request."
                onClick={() => setRequestToRemove(request)}
                onAction={handleDeleteAction}
              />
              <Button
                variant="secondary"
                className="gap-2 w-fit"
                onClick={() => setIsEditing((value) => !value)}
              >
                <PencilIcon size={16} />
                Edit details
              </Button>
            </div>
          </>
        )}
      </SheetHeader>

      <div className="flex flex-row items-center justify-between pl-3 pr-4 gap-2">
        <div className="flex flex-row mr-auto">
          <RequestVoteCount count={request.voteCount} className="justify-start" />
          <div className="flex flex-col justify-center">
            <span className="ml-2">Votes</span>
            {/* TODO: Add manage votes later */}
            {/* <span className="text-muted-foreground text-sm ml-2 relative top-[6px]">Votes</span> */}
            {/* <Button className="px-2" variant="link">
              Manage votes
            </Button> */}
          </div>
        </div>
        <ChangePriorityDropdown
          currentPriority={request.priority}
          onSelect={(priority) => handleChangePriority(request, priority)}
          popperContentProps={{ align: 'start' }}
        >
          <Button className="h-9 min-w-9 p-2" variant="outline">
            {priority && (
              <div className="flex items-center gap-1">
                <priority.Icon className="mr-0.5 shrink-0" size={16} />
                {priority.label}
                <ChevronDownIcon className="text-muted-foreground" size={18} />
              </div>
            )}
          </Button>
        </ChangePriorityDropdown>
        <ChangeStatusDropdown
          currentStatus={request.status}
          isWithRemoveOption={false}
          onSelect={(status) => handleChangeStatus(request, status)}
          onRemove={() => setRequestToRemove(request)}
          popperContentProps={{ align: 'start' }}
        >
          <Button className="h-9 min-w-9 p-2" variant="outline">
            {status && (
              <div className="flex items-center gap-1">
                <status.Icon className={`${status.iconClassName} mr-0.5 shrink-0`} size={16} />
                {status.label}
                <ChevronDownIcon className="text-muted-foreground" size={18} />
              </div>
            )}
          </Button>
        </ChangeStatusDropdown>
      </div>

      <Separator />

      <div className="flex flex-col px-4 gap-4">
        <Collapsible
          open={isRequestDetailsOpen}
          onOpenChange={setIsRequestDetailsOpen}
          className="flex flex-col gap-2"
        >
          <CollapsibleTrigger
            asChild
            className="py-3 cursor-pointer hover:bg-muted/30 transition-colors"
          >
            <button className="flex items-center justify-between rounded-md gap-2 px-2 mx-[-8px]">
              <div className="flex items-center gap-3 font-medium">
                <InfoIcon className="text-muted-foreground" size={18} />
                Request details
              </div>
              <ChevronDownIcon
                className={`${!isRequestDetailsOpen ? 'rotate-[-90deg]' : ''} text-muted-foreground transition-transform`}
                size={18}
              />
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="flex flex-col pb-8">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-2">
                <Label className="text-muted-foreground text-sm">Author</Label>
                <div className="flex items-center gap-2">
                  <UserAvatar userId={request.userId} size={24} />
                  {request.userId === EMPTY_UUID ? (
                    <span>Created from admin console</span>
                  ) : (
                    <>
                      <Link
                        to="/projects/$projectId/users/$userId"
                        params={{ projectId, userId: request.userId }}
                        className="underline text-sm underline-offset-2"
                      >
                        <span>{request?.user?.email || request.userId}</span>
                      </Link>
                      {request.user?.email ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a href={`mailto:${request.user?.email}`}>
                              <MailPlusIcon size={22} />
                            </a>
                          </TooltipTrigger>
                          <TooltipContent>Send email to {request.user?.email}</TooltipContent>
                        </Tooltip>
                      ) : null}
                    </>
                  )}
                </div>
              </div>

              <Separator className="opacity-70" />

              <div className="flex flex-col gap-2">
                <Label className="text-muted-foreground text-sm">Platform source</Label>
                <RequestPlatform platform={request.platform} />
              </div>

              <Separator className="opacity-70" />

              <div className="flex flex-col gap-1">
                <Label className="text-muted-foreground text-sm">Metadata</Label>
                <pre className="bg-secondary/60 rounded-md p-2 text-sm overflow-x-auto">
                  {JSON.stringify(request.meta, null, 2)}
                </pre>
              </div>

              <Separator className="opacity-70" />

              <div className="flex flex-row items-center justify-between gap-2">
                <div>
                  <Label className="text-muted-foreground text-sm">Request ID</Label>
                  <span className="text-sm">{request.id}</span>
                </div>
                <CopyToClipboardButton
                  value={request.id}
                  toastMessage="Request ID copied to clipboard"
                />
              </div>

              <Separator className="opacity-70" />

              <div>
                <Label className="text-muted-foreground text-sm">Created on</Label>
                <span className="text-sm">
                  {format(request.createdAt, 'LLL dd, y')}{' '}
                  <span className="text-muted-foreground">
                    ({formatDistanceToNow(request.createdAt)} ago)
                  </span>
                </span>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>

      <Separator />

      {isSubscriptionPending || !subscription ? null : (
        <RequestComments
          isCommentsFetching={isCommentsFetching}
          isFreePlan={subscription.isFree}
          comments={comments}
          request={request}
        />
      )}
    </>
  );
};
